import {
    Badge,
    Box,
    Button,
    Divider,
    HStack,
    SimpleGrid,
    Text,
    Textarea,
    VStack,
    useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { translate } from "../../../i18n";
import {
    SignupDocumentStatus,
    SignupDocumentVerificationResult,
} from "../../../models/user/dto/user.dto";
import { useApproveSignupDocument } from "../hooks/useApproveSignupDocument";
import { useGetUserMemberships } from "../hooks/useGetUserMemberships";
import { useRejectSignupDocument } from "../hooks/useRejectSignupDocument";

type Props = {
    userId: string;
    firstName: string;
    lastName: string;
    status: SignupDocumentStatus | null | undefined;
    result: SignupDocumentVerificationResult | null | undefined;
    verifiedAt: string | null | undefined;
};

const STATUS_BADGE_COLORS: Record<SignupDocumentStatus, string> = {
    pending: 'yellow',
    verified: 'green',
    rejected: 'red',
    needs_review: 'orange',
};

const statusBadgeLabel = (status: SignupDocumentStatus): string => {
    switch (status) {
        case 'pending':
            return translate('organization.document_status_pending');
        case 'verified':
            return translate('organization.document_status_verified');
        case 'rejected':
            return translate('organization.document_status_rejected');
        case 'needs_review':
            return translate('organization.document_status_needs_review');
    }
};

export function SignupDocumentVerificationPanel({
    userId,
    firstName,
    lastName,
    status,
    result,
    verifiedAt,
}: Props) {
    const toast = useToast();
    const [rejectReason, setRejectReason] = useState('');
    const { data: memberships } = useGetUserMemberships(userId);
    const assignedMembership = memberships?.find(
        (m) => m.id === result?.assignedMembershipId,
    );
    const approve = useApproveSignupDocument(
        userId,
        () =>
            toast({
                title: translate('organization.verification_approved_toast'),
                status: 'success',
                position: 'top-right',
            }),
        (err) =>
            toast({
                title: translate('organization.verification_approve_failed_toast'),
                description: err.message,
                status: 'error',
                position: 'top-right',
            }),
    );
    const reject = useRejectSignupDocument(
        userId,
        () => {
            setRejectReason('');
            toast({
                title: translate('organization.verification_rejected_toast'),
                status: 'success',
                position: 'top-right',
            });
        },
        (err) =>
            toast({
                title: translate('organization.verification_reject_failed_toast'),
                description: err.message,
                status: 'error',
                position: 'top-right',
            }),
    );

    if (!status) {
        return (
            <Box mt={3}>
                <Text fontSize="sm" color="gray.500">
                    {translate('organization.verification_not_run')}
                </Text>
            </Box>
        );
    }

    const badge = {
        colorScheme: STATUS_BADGE_COLORS[status],
        label: statusBadgeLabel(status),
    };
    const extracted = result?.extracted;
    const match = result?.match;

    const canReview = status === 'needs_review' || status === 'rejected';

    return (
        <Box mt={4}>
            <Divider mb={4} />
            <HStack mb={2}>
                <Text fontWeight="bold">
                    {translate('organization.verification_status_label')}
                </Text>
                <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                {verifiedAt && (
                    <Text fontSize="xs" color="gray.500">
                        ({new Date(verifiedAt).toLocaleString()})
                    </Text>
                )}
            </HStack>

            {result?.reason && (
                <Text fontSize="sm" color="gray.600" mb={1}>
                    {translate('organization.verification_reason_label')}{' '}
                    <code>{result.reason}</code>
                </Text>
            )}

            {result?.actor?.adminEmail && (
                <Text fontSize="xs" color="gray.500" mb={2}>
                    {translate('organization.verification_acted_by')} {result.actor.adminEmail}
                </Text>
            )}

            {status === 'verified' && result?.assignedMembershipId && (
                <Text fontSize="sm" color="gray.700" mb={3}>
                    {translate('organization.verification_auto_assigned_membership')}{' '}
                    <strong>
                        {assignedMembership?.type?.name ?? result.assignedMembershipId}
                    </strong>
                </Text>
            )}

            {extracted && (
                <SimpleGrid columns={2} spacing={3} mt={3} fontSize="sm">
                    <ComparisonRow
                        label={translate('organization.verification_first_name')}
                        extracted={extracted.firstName}
                        profile={firstName}
                        confidence={match?.firstNameConfidence}
                    />
                    <ComparisonRow
                        label={translate('organization.verification_last_name')}
                        extracted={extracted.lastName}
                        profile={lastName}
                        confidence={match?.lastNameConfidence}
                    />
                    <KvRow
                        label={translate('organization.verification_document_type')}
                        value={extracted.documentType}
                    />
                    <KvRow
                        label={translate('organization.verification_expiry')}
                        value={extracted.expiryDate}
                    />
                    <KvRow
                        label={translate('organization.verification_institution')}
                        value={extracted.institution}
                    />
                    <KvRow
                        label={translate('organization.verification_unique_id')}
                        value={extracted.uniqueId}
                    />
                    <KvRow
                        label={translate('organization.verification_genuine_confidence')}
                        value={
                            extracted.genuineConfidence !== null && extracted.genuineConfidence !== undefined
                                ? extracted.genuineConfidence.toFixed(2)
                                : null
                        }
                    />
                    <KvRow
                        label={translate('organization.verification_photo_of_screen')}
                        value={
                            extracted.isPhotoOfScreen === null || extracted.isPhotoOfScreen === undefined
                                ? null
                                : extracted.isPhotoOfScreen
                                ? translate('organization.verification_yes')
                                : translate('organization.verification_no')
                        }
                    />
                </SimpleGrid>
            )}

            {(status === 'needs_review' || status === 'verified') && (
                <Textarea
                    mt={4}
                    size="sm"
                    rows={2}
                    maxLength={512}
                    placeholder={translate('organization.verification_reject_reason_placeholder')}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                />
            )}

            <HStack mt={4} spacing={3}>
                {canReview && (
                    <Button
                        size="sm"
                        colorScheme="green"
                        isLoading={approve.isLoading}
                        onClick={() => approve.mutate()}
                    >
                        {translate('organization.verification_approve')}
                    </Button>
                )}
                {(status === 'needs_review' || status === 'verified') && (
                    <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        isLoading={reject.isLoading}
                        onClick={() =>
                            reject.mutate({
                                reason: rejectReason.trim() || 'admin_rejected',
                            })
                        }
                    >
                        {translate('organization.verification_reject')}
                    </Button>
                )}
            </HStack>
        </Box>
    );
}

function ComparisonRow({
    label,
    extracted,
    profile,
    confidence,
}: {
    label: string;
    extracted: string | null | undefined;
    profile: string;
    confidence: number | undefined;
}) {
    return (
        <VStack align="stretch" spacing={0}>
            <Text fontWeight="semibold" fontSize="xs" color="gray.500">{label}</Text>
            <Text>
                {extracted ?? <em>{translate('organization.verification_not_extracted')}</em>}{' '}
                <Text as="span" color="gray.400">{translate('organization.verification_vs')}</Text>{' '}
                {profile}
                {confidence !== undefined && (
                    <Text as="span" ml={2} color="gray.500" fontSize="xs">
                        ({(confidence * 100).toFixed(0)}% {translate('organization.verification_match_label')})
                    </Text>
                )}
            </Text>
        </VStack>
    );
}

function KvRow({ label, value }: { label: string; value: string | null | undefined }) {
    return (
        <VStack align="stretch" spacing={0}>
            <Text fontWeight="semibold" fontSize="xs" color="gray.500">{label}</Text>
            <Text>{value ?? <em>—</em>}</Text>
        </VStack>
    );
}
